import React, { useState, useEffect } from 'react';
import api from '../api';

const FeeTracking = () => {
    const [fees, setFees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedFee, setSelectedFee] = useState(null);
    const [showQR, setShowQR] = useState(false);
    const [showPaymentOptions, setShowPaymentOptions] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [loadingQR, setLoadingQR] = useState(false);
    const [loadingVNPay, setLoadingVNPay] = useState(false);
    const [customAmount, setCustomAmount] = useState('');

    useEffect(() => {
        fetchFees();
    }, []);

    const fetchFees = async () => {
        try {
            const res = await api.get('/fees');
            setFees(res.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching fees:', err);
            setLoading(false);
        }
    };

    const generateQRCode = async (fee, amount) => {
        setLoadingQR(true);
        try {
            // Use provided amount or calculate remaining amount
            const amountToPay = amount || fee.remainingAmount || (fee.amount - (fee.paidAmount || 0));
            const content = `HOC PHI ${fee.class?.name || 'LMS'}`;

            const response = await fetch('https://open.oapi.vn/banking/generate-qr', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    bin: '970436',
                    accountNo: '1014942198',
                    accountName: 'NGUYEN HUU HOANG MINH',
                    amount: amountToPay,
                    content: content,
                }),
            });

            const data = await response.json();

            if (data.code === 'success' && data.data) {
                setQrCodeUrl(data.data);
            } else {
                console.error('Failed to generate QR code:', data);
                setQrCodeUrl('');
            }
        } catch (error) {
            console.error('Error generating QR code:', error);
            setQrCodeUrl('');
        } finally {
            setLoadingQR(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'paid':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'partial':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'overdue':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            default:
                return 'bg-slate-100 text-slate-800 border-slate-200';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'paid':
                return 'Đã thanh toán';
            case 'partial':
                return 'Thanh toán một phần';
            case 'overdue':
                return 'Quá hạn';
            case 'pending':
                return 'Chưa thanh toán';
            default:
                return status;
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
        }).format(amount);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    };

    const handleShowQR = async (fee) => {
        setSelectedFee(fee);
        setShowQR(true);
        const maxAmount = fee.remainingAmount || (fee.amount - (fee.paidAmount || 0));
        setCustomAmount(maxAmount.toString());
        await generateQRCode(fee, maxAmount);
    };

    const handleAmountChange = (e) => {
        const value = e.target.value;
        setCustomAmount(value);
    };

    const handleGenerateQR = () => {
        const amount = parseInt(customAmount);
        const maxAmount = selectedFee.remainingAmount || (selectedFee.amount - (selectedFee.paidAmount || 0));

        if (amount > 0 && amount <= maxAmount) {
            generateQRCode(selectedFee, amount);
        }
    };

    const handleVNPayPayment = async (fee) => {
        setLoadingVNPay(true);
        try {
            const response = await api.post('/vnpay/create-payment', {
                feeId: fee._id
            });

            if (response.data.success && response.data.paymentUrl) {
                // Redirect to VNPay payment page
                window.location.href = response.data.paymentUrl;
            } else {
                alert('Không thể tạo liên kết thanh toán. Vui lòng thử lại.');
            }
        } catch (error) {
            console.error('Error creating VNPay payment:', error);
            alert('Lỗi khi tạo thanh toán: ' + (error.response?.data?.msg || error.message));
        } finally {
            setLoadingVNPay(false);
        }
    };

    const handleShowPaymentOptions = (fee) => {
        setSelectedFee(fee);
        setShowPaymentOptions(true);
    };

    const totalOwed = fees
        .filter((fee) => fee.status !== 'paid')
        .reduce((sum, fee) => sum + fee.amount, 0);

    const totalPaid = fees
        .filter((fee) => fee.status === 'paid')
        .reduce((sum, fee) => sum + fee.amount, 0);

    if (loading) {
        return (
            <div className="rounded-2xl bg-white p-6 border border-slate-200">
                <div className="flex items-center justify-center py-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-2xl bg-white p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-slate-900">Học phí</h2>
                <i className="fas fa-wallet text-2xl text-indigo-600"></i>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="rounded-xl bg-gradient-to-br from-red-50 to-red-100 p-4 border border-red-200">
                    <div className="text-sm font-medium text-red-700 mb-1">Chưa thanh toán</div>
                    <div className="text-2xl font-bold text-red-900">{formatCurrency(totalOwed)}</div>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-green-50 to-green-100 p-4 border border-green-200">
                    <div className="text-sm font-medium text-green-700 mb-1">Đã thanh toán</div>
                    <div className="text-2xl font-bold text-green-900">{formatCurrency(totalPaid)}</div>
                </div>
            </div>

            {/* Fee List */}
            {fees.length === 0 ? (
                <div className="text-center py-8">
                    <i className="fas fa-receipt text-4xl text-slate-300 mb-3"></i>
                    <p className="text-slate-500">Chưa có khoản học phí nào</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {fees.map((fee) => (
                        <div
                            key={fee._id}
                            className="rounded-lg border border-slate-200 p-4 transition hover:shadow-md"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="font-semibold text-slate-900">
                                            {fee.description}
                                        </h3>
                                        <span
                                            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(
                                                fee.status
                                            )}`}
                                        >
                                            {getStatusText(fee.status)}
                                        </span>
                                    </div>
                                    <div className="space-y-1 text-sm text-slate-600">
                                        <div className="flex items-center gap-2">
                                            <i className="fas fa-book w-4"></i>
                                            <span>{fee.class?.name || 'N/A'}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <i className="fas fa-calendar w-4"></i>
                                            <span>Hạn: {formatDate(fee.dueDate)}</span>
                                        </div>
                                        {fee.paidDate && (
                                            <div className="flex items-center gap-2 text-green-600">
                                                <i className="fas fa-check-circle w-4"></i>
                                                <span>Đã thanh toán: {formatDate(fee.paidDate)}</span>
                                            </div>
                                        )}
                                        {fee.note && (
                                            <div className="flex items-start gap-2 text-slate-500 italic">
                                                <i className="fas fa-sticky-note w-4 mt-0.5"></i>
                                                <span>{fee.note}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <div className="text-xl font-bold text-indigo-600">
                                        {formatCurrency(fee.amount)}
                                    </div>
                                    {fee.paidAmount > 0 && fee.status !== 'paid' && (
                                        <div className="text-right text-xs text-slate-600">
                                            <div>Đã trả: {formatCurrency(fee.paidAmount)}</div>
                                            <div className="font-semibold text-orange-600">
                                                Còn lại: {formatCurrency(fee.remainingAmount || (fee.amount - fee.paidAmount))}
                                            </div>
                                        </div>
                                    )}
                                    {fee.status !== 'paid' && (
                                        <button
                                            onClick={() => handleShowPaymentOptions(fee)}
                                            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-indigo-700"
                                        >
                                            <i className="fas fa-credit-card"></i>
                                            <span>Thanh toán</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Payment Options Modal */}
            {showPaymentOptions && selectedFee && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={() => setShowPaymentOptions(false)}
                >
                    <div
                        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowPaymentOptions(false)}
                            className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200"
                        >
                            <i className="fas fa-times"></i>
                        </button>

                        <h3 className="text-xl font-bold text-slate-900 mb-2">
                            Chọn phương thức thanh toán
                        </h3>
                        <p className="text-sm text-slate-600 mb-6">
                            {selectedFee.description} - {formatCurrency(selectedFee.remainingAmount || (selectedFee.amount - (selectedFee.paidAmount || 0)))}
                        </p>

                        <div className="space-y-3">
                            {/* VNPay Payment Option */}
                            <button
                                onClick={() => {
                                    setShowPaymentOptions(false);
                                    handleVNPayPayment(selectedFee);
                                }}
                                disabled={loadingVNPay}
                                className="w-full flex items-center gap-4 rounded-xl border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-indigo-100 p-4 transition hover:border-indigo-300 hover:shadow-md disabled:opacity-50"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-600 text-white">
                                    <i className="fas fa-credit-card text-xl"></i>
                                </div>
                                <div className="flex-1 text-left">
                                    <div className="font-semibold text-slate-900">VNPay</div>
                                    <div className="text-sm text-slate-600">
                                        {loadingVNPay ? 'Đang tạo thanh toán...' : 'Thẻ ATM, Visa, Mastercard'}
                                    </div>
                                </div>
                                <i className="fas fa-chevron-right text-slate-400"></i>
                            </button>

                            {/* QR Code Option */}
                            <button
                                onClick={() => {
                                    setShowPaymentOptions(false);
                                    handleShowQR(selectedFee);
                                }}
                                className="w-full flex items-center gap-4 rounded-xl border-2 border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100 p-4 transition hover:border-slate-300 hover:shadow-md"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-700 text-white">
                                    <i className="fas fa-qrcode text-xl"></i>
                                </div>
                                <div className="flex-1 text-left">
                                    <div className="font-semibold text-slate-900">Mã QR ngân hàng</div>
                                    <div className="text-sm text-slate-600">Quét QR để chuyển khoản</div>
                                </div>
                                <i className="fas fa-chevron-right text-slate-400"></i>
                            </button>
                        </div>

                        <div className="mt-6 rounded-lg bg-blue-50 p-3 border border-blue-200">
                            <div className="flex gap-2">
                                <i className="fas fa-info-circle text-blue-600 mt-0.5"></i>
                                <div className="text-xs text-blue-800">
                                    <strong>VNPay:</strong> Thanh toán tự động, xác nhận ngay lập tức<br/>
                                    <strong>QR Code:</strong> Cần admin xác nhận sau khi chuyển khoản
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* QR Code Modal */}
            {showQR && selectedFee && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={() => setShowQR(false)}
                >
                    <div
                        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowQR(false)}
                            className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200"
                        >
                            <i className="fas fa-times"></i>
                        </button>

                        <h3 className="text-xl font-bold text-slate-900 mb-4">
                            Thanh toán học phí
                        </h3>

                        <div className="space-y-4">
                            {/* Amount Input */}
                            <div className="rounded-lg bg-slate-50 p-4">
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Số tiền thanh toán (VNĐ)
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="number"
                                        value={customAmount}
                                        onChange={handleAmountChange}
                                        min="1"
                                        max={selectedFee.remainingAmount || (selectedFee.amount - (selectedFee.paidAmount || 0))}
                                        step="1000"
                                        className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                        placeholder="Nhập số tiền"
                                    />
                                    <button
                                        onClick={handleGenerateQR}
                                        className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-indigo-700"
                                    >
                                        <i className="fas fa-sync-alt"></i>
                                        Tạo QR
                                    </button>
                                </div>
                                <p className="mt-2 text-xs text-slate-500">
                                    Tối đa: {formatCurrency(selectedFee.remainingAmount || (selectedFee.amount - (selectedFee.paidAmount || 0)))}
                                </p>
                            </div>

                            {/* QR Code */}
                            <div className="flex flex-col items-center">
                                <div className="rounded-lg bg-white p-4 shadow-lg border-2 border-indigo-200">
                                    {loadingQR ? (
                                        <div className="w-80 h-80 flex items-center justify-center">
                                            <div className="h-12 w-12 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
                                        </div>
                                    ) : qrCodeUrl ? (
                                        <img
                                            src={qrCodeUrl}
                                            alt="QR Code thanh toán"
                                            className="w-80 h-80 object-contain"
                                            onError={(e) => {
                                                console.error('QR code image failed to load');
                                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="320"%3E%3Crect fill="%23f1f5f9" width="320" height="320"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="16" fill="%2364748b" text-anchor="middle" dominant-baseline="middle"%3EKhông thể tải QR%3C/text%3E%3C/svg%3E';
                                            }}
                                        />
                                    ) : (
                                        <div className="w-80 h-80 flex items-center justify-center text-slate-500">
                                            Không thể tạo mã QR
                                        </div>
                                    )}
                                </div>
                                <p className="text-sm text-slate-600 mt-3 text-center">
                                    Quét mã QR bằng ứng dụng ngân hàng để thanh toán
                                </p>
                            </div>

                            <div className="text-xs text-slate-500 text-center">
                                Sau khi chuyển khoản, vui lòng chờ admin xác nhận thanh toán
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeeTracking;
